import { PRINTED_QR_PATH } from '@croissants/contracts/qr';
import type { AddressBar } from '@croissants/ui';

/**
 * The QR token inside the URL a scan actually opens (PRD GST-001, OPS-019).
 *
 * The printed sheet encodes `<guest origin>/q/<token>` - `PRINTED_QR_PATH` is
 * the SHARED constant the sheet builder (`services` qr-batch) also imports, so
 * the surface that prints the code and this parser, the surface that receives
 * the scan, can never drift apart again. They did drift once: the sheets
 * encoded `/q/` while this app parsed only `/s/` and the query forms, so every
 * printed sheet landed on the no-code screen on a clean phone - and on a phone
 * that had scanned a DIFFERENT apartment earlier, the stale stored code was
 * silently served instead, addressing the order to the wrong flat (TST-008).
 * Printed codes are screwed to apartment walls and cannot be recalled, which is
 * why the parser side widened rather than the sheets changing.
 *
 * Accepted shapes, most common first:
 *
 *  - the printed path `/q/<token>` (what every sheet in circulation encodes);
 *  - the legacy path `/s/<token>` (kept working for anything already shared);
 *  - the query forms `?q=<token>` and `?qr=<token>` (deep links, dev tooling).
 *
 * A token found in the URL is a scan that JUST happened, and the caller treats
 * it as authoritative: it REPLACES whatever code this phone had stored.
 */

/** Legacy entry path, kept alive for links already in circulation. */
const LEGACY_QR_PATH = '/s/';

const TOKEN_PATHS: readonly string[] = [PRINTED_QR_PATH, LEGACY_QR_PATH];

/** Reads the QR token out of the current URL, in any shape a scan produces. */
export function qrTokenFromLocation(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const fromQuery = url.searchParams.get('q') ?? url.searchParams.get('qr');
  if (fromQuery !== null && fromQuery.trim() !== '') return fromQuery.trim();
  for (const prefix of TOKEN_PATHS) {
    if (!url.pathname.startsWith(prefix)) continue;
    const raw = url.pathname.slice(prefix.length).split('/')[0] ?? '';
    if (raw === '') continue;
    try {
      return decodeURIComponent(raw);
    } catch {
      // A stray '%' is not a reason to drop the scan; pass it through as-is.
      return raw;
    }
  }
  return null;
}

/** True when this pathname is a token-bearing entry path, printed or legacy. */
export function isQrEntryPath(pathname: string): boolean {
  return TOKEN_PATHS.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Takes the scanned code out of the address bar - the ONE place the guest does
 * it, for a scan just read and for `forget` alike. Strips the token in every
 * shape `qrTokenFromLocation` accepts, landing entry paths on `/` so the router
 * opens the storefront. The token is a bearer credential: it has no business
 * sitting in a screenshot, a browser history entry or a shared link (GST-024,
 * NFR-009), so the entry's state is dropped (`replaceState(null, ...)`) rather
 * than carried forward - unlike `consumeSearchParam` (packages/ui
 * address-bar.ts), which keeps it. A host with no address bar is left alone.
 */
export function scrubQrFromAddressBar(bar: AddressBar = globalThis as AddressBar): void {
  const { location, history } = bar;
  if (location === undefined || history === undefined) return;
  const url = new URL(location.href);
  url.searchParams.delete('q');
  url.searchParams.delete('qr');
  if (isQrEntryPath(url.pathname)) url.pathname = '/';
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}
